/**
 * Web Vitals (LCP, INP, CLS) для RUM і для заміру в Playwright. Бібліотека вантажиться
 * ліниво після першого екрана: вона не входить у бюджет першого екрана, а буферизовані
 * записи браузера (LCP, event timing) вона все одно прочитає. Відправку на сервер (вибірково,
 * без ПД) додасть тікет RUM, групуючи за `browser` (родина й мажорна версія, UX-52); поки
 * значення лише в `window.__clVitals` і подіях `cl:vital`.
 */

export interface VitalsSnapshot {
  /** Родина й мажорна версія браузера для агрегатів (`Chrome 141`, `other`); рядка UA немає. */
  browser?: string;
  LCP?: number;
  INP?: number;
  CLS?: number;
}

declare global {
  interface Window {
    __clVitals?: VitalsSnapshot;
  }
}

/**
 * `bucket` — лінивий розбір браузера (`support/browser.ts`). Його дає лише `main.tsx`: документ входу
 * його не передає, бо спільний динамічний імпорт з банером ліг би в чанк `shared`, і `/login` вантажив
 * би код застосунку (brand.spec: «вхід — окремий документ»).
 *
 * `when` — коли вантажити бібліотеку. За замовчуванням — після `load` і простою (документ входу);
 * застосунок передає межу першого екрана (`afterFirstScreen`, perf/first-screen.ts): простій після
 * `load` там настає ще до даних стартового екрана, і бібліотека була б запитом першого екрана.
 */
export function startRum(
  bucket?: () => Promise<string>,
  when: (run: () => void) => void = afterLoad,
) {
  const vitals: VitalsSnapshot = {};
  window.__clVitals = vitals;
  const run = () =>
    Promise.all([import('web-vitals'), bucket?.()]).then(([{ onCLS, onINP, onLCP }, browser]) => {
      if (browser) vitals.browser = browser;
      const report = (m: { name: string; value: number }) => {
        vitals[m.name as 'LCP' | 'INP' | 'CLS'] = m.value;
        window.dispatchEvent(new CustomEvent('cl:vital', { detail: m }));
      };
      const opts = { reportAllChanges: true };
      onLCP(report, opts);
      onINP(report, opts);
      onCLS(report, opts);
    });
  when(() => void run());
}

/** Після `load` і простою. */
function afterLoad(run: () => void) {
  const idle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1));
  if (document.readyState === 'complete') idle(run);
  else window.addEventListener('load', () => idle(run), { once: true });
}
